import { withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import { useFeatureFlags } from '@jusbrasil-web/shared-feature-flags'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { GetServerSideProps } from 'next'
import { useCallback, useState } from 'react'

import { internalAuthApi } from '../../../api'
import { AuthLayout, LoginCard, SignupCpfForm } from '../../../components'
import { ROUTES } from '../../../constants'
import {
  type SignupCpfFieldsFlags,
  type SignupCpfFieldsVariant,
  isSignupCpfFieldsVariant,
  resolveSignupCpfFieldsLayout,
  resolveSignupCpfFieldsVariant,
  SIGNUP_CPF_FIELDS_FLAG_KEY,
} from '../../../experiments/signup-cpf-fields'
import { withCampaignFallback } from '../../../utils/campaign-attribution'
import { ERROR_MESSAGES } from '../../../utils/errorMessages'
import type { AuthPageEventData } from '../../../utils/event-data'
import { getEventDataFromQuery } from '../../../utils/event-data'
import { getVerifiedSignupEmail } from '../../../utils/get-verified-signup-email'
import { logNextUrlCollapse } from '../../../utils/next-url-collapse-logger'
import { parseErrorMessage } from '../../../utils/parseErrorMessage'
import { isValidRedirectUrl } from '../../../utils/redirect-url'
import { fireSignUpFormPageSeenServer } from '../../../utils/server-side-argos-events'
import styles from './index.module.scss'

export interface SignupCpfPageProps {
  email: string
  signUpByEmailToken: string | null
  nextUrl?: string | null
  csrfToken?: string | null
  isGoogleFlow?: boolean
  eventData?: AuthPageEventData
  requiresPhoneStep?: boolean
  signupCpfFieldsForcedVariant?: SignupCpfFieldsVariant | null
  signupCpfFieldsInExperiment?: boolean
}

export function SignupCpfPage({
  email,
  signUpByEmailToken,
  nextUrl,
  isGoogleFlow = false,
  requiresPhoneStep = false,
  eventData,
  signupCpfFieldsForcedVariant = null,
  signupCpfFieldsInExperiment = false,
}: SignupCpfPageProps) {
  // Reading the variant here — client-side, on a page that always renders the
  // form — is what fires Experiment.Participate (once per key, deduped by the
  // provider). The SSR resolver never reads it, so exposure lines up with a
  // form the user actually saw. See docs/analytics.md.
  const { getFeatureValue } = useFeatureFlags<SignupCpfFieldsFlags>()
  const flagVariant = signupCpfFieldsForcedVariant
    ? undefined
    : getFeatureValue(SIGNUP_CPF_FIELDS_FLAG_KEY)
  const signupCpfFieldsVariant: SignupCpfFieldsVariant | null =
    signupCpfFieldsForcedVariant ?? (isSignupCpfFieldsVariant(flagVariant) ? flagVariant : null)
  // Single owner of the layout: the page decides here and hands it to the form.
  const layout = resolveSignupCpfFieldsLayout(signupCpfFieldsVariant)

  const [error, setError] = useState<string | undefined>()
  const [isBelowMinimumAgeBlocked, setIsBelowMinimumAgeBlocked] = useState(false)
  const parsedError = error ? parseErrorMessage(error) : null

  const handleBelowMinimumAgeError = useCallback((blocked: boolean) => {
    setIsBelowMinimumAgeBlocked(blocked)
  }, [])

  const belowMinimumAgeMessage = isBelowMinimumAgeBlocked
    ? ERROR_MESSAGES.signup.cpf.birthDate.belowMinimumAge
    : null

  const errorMessage =
    belowMinimumAgeMessage ??
    (parsedError ? { title: parsedError.title, description: parsedError.description } : undefined)

  const header = {
    title: 'Finalize seu cadastro',
    subtitle: {
      content:
        'Precisamos desses dados para identificar seus processos e exibir as movimentações para você.',
    },
    // Arm A onwards drops the chip: the user already confirmed this e-mail, so it
    // only pushes the fields that matter further down.
    ...(layout.showEmailChip &&
      email && {
        chip: {
          text: email,
        },
      }),
    className: styles.headerMargin,
    ...(errorMessage && { errorMessage }),
  }

  return (
    <AuthLayout>
      <LoginCard header={header} hideCloseButton>
        <SignupCpfForm
          signUpByEmailToken={signUpByEmailToken}
          email={email}
          nextUrl={nextUrl}
          onError={setError}
          onBelowMinimumAgeError={handleBelowMinimumAgeError}
          isGoogleFlow={isGoogleFlow}
          requiresPhoneStep={requiresPhoneStep}
          eventData={eventData}
          experiment={{
            variant: signupCpfFieldsVariant,
            inExperiment: signupCpfFieldsInExperiment,
          }}
          layout={layout}
        />
      </LoginCard>
    </AuthLayout>
  )
}

const GOOGLE_SIGNUP_COOKIE = 'signup_data_g_token'

const getServerSideProps: GetServerSideProps = async (ctx) => {
  const signUpByEmailToken = getFirstQueryParam(ctx.query.signUpByEmailToken) || null
  const rawNextUrl = getFirstQueryParam(ctx.query.next_url) ?? null
  const nextUrl = rawNextUrl && isValidRedirectUrl(rawNextUrl) ? rawNextUrl : null
  logNextUrlCollapse({ step: 'cpf', raw: rawNextUrl, jdid: ctx.req.cookies?.jdid ?? null })
  const hasGoogleSignupCookie = !!ctx.req.cookies[GOOGLE_SIGNUP_COOKIE]

  let email: string | null = null

  if (signUpByEmailToken) {
    const result = await getVerifiedSignupEmail(signUpByEmailToken, ctx)
    if (!result || !result.response.ok) {
      return {
        redirect: {
          destination: ROUTES.SIGN_UP.BEGIN,
          permanent: false,
        },
      }
    }
    const { email: verifiedEmail } = result
    email = verifiedEmail
  }

  if (!email && !hasGoogleSignupCookie) {
    return {
      redirect: {
        destination: ROUTES.SIGN_UP.BEGIN,
        permanent: false,
      },
    }
  }

  const isGoogleFlow = !signUpByEmailToken && hasGoogleSignupCookie
  const isB2BScope = getFirstQueryParam(ctx.query.scope).trim().toLowerCase() === 'b2b'

  // Both stay below the redirect guards on purpose: withApollo warns after 5s
  // when withApolloProps() is never called (every redirect path above), and the
  // experiment must not resolve for a request that never renders the form.
  // The Apollo client is inert here — this page has no pid, so the viewer query
  // inside withFeatureFlags never runs; it only satisfies the signature.
  const { client, withApolloProps } = withApollo(ctx)
  const [{ csrfToken }, signupCpfFields] = await Promise.all([
    internalAuthApi.getCsrfToken(ctx),
    resolveSignupCpfFieldsVariant({ ctx, apolloClient: client }),
  ])
  const eventData = withCampaignFallback(
    getEventDataFromQuery(ctx.query, { email: email ?? undefined }),
    ctx.query,
    ctx
  )

  fireSignUpFormPageSeenServer({
    req: ctx.req,
    email: email ?? '',
    eventData,
    type: isGoogleFlow ? 'social' : 'default',
    ...(isGoogleFlow && { social: 'Google' }),
  })

  return {
    props: {
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      ...withApolloProps(),
      ...signupCpfFields.featureFlagsProps,
      signUpByEmailToken,
      nextUrl,
      email: email ?? '',
      csrfToken,
      isGoogleFlow,
      eventData,
      requiresPhoneStep: !isB2BScope,
      signupCpfFieldsForcedVariant: signupCpfFields.forcedVariant,
      signupCpfFieldsInExperiment: signupCpfFields.inExperiment,
    },
  }
}

export const SignupCpfPageExports = {
  getServerSideProps,
}
